import { Client } from "@twilio/conversations";
import { Dispatch } from "redux";
import log from "loglevel";

import { sessionDataHandler } from "../../../sessionDataHandler";
import { notifications } from "../../../notifications";
import { addNotification, removeNotification } from "../genericActions";
import { initSession } from "../initActions";
import { revalidarEstadoDaConversa } from "./revalidarConversa";
import { descreverErro } from "../../../utils/descreverErro";
import { reportClientError } from "../../../utils/reportClientError";

/**
 * Reconexão automática quando o SDK do Twilio DESISTE por conta própria.
 *
 * Sem isto o apostador ficava com o chat mudo até dar F5 — e nada na tela dizia isso: o
 * `connectionStateChanged` só tratava `connected`/`connecting`, então `disconnected`/`denied`/`error`
 * caíam no vácuo.
 *
 * Dois níveis, do mais barato pro mais caro:
 *  - **Tier 1** — token novo + `updateToken`. Não recria nada, então não há risco de duplicar.
 *  - **Tier 2** — reconstrói a sessão. Só depois do Tier 1 falhar, porque é aqui que mora o risco.
 */

/** Estados em que o SDK parou de tentar por conta própria — só estes disparam a reconexão. */
const ESTADOS_QUE_DISPARAM = ["disconnected", "denied", "error"];

/**
 * De onde nasceu a tentativa.
 *
 * ⚠️ Este vocabulário existe em DOIS lugares: aqui e na allowlist `VALORES.origemDaReconexao` do
 * `server/controllers/clientErrorController.js`. Acrescentar valor num lado e esquecer o outro faz
 * o backend DESCARTAR o valor em silêncio — o alerta chega sem rótulo e ninguém percebe. Mexeu
 * aqui, mexa lá (e vice-versa).
 */
export type OrigemDaReconexao = "desligamento_proprio" | "conexao_perdida" | "volta_de_aba";

/*
 * Marca presa à IDENTIDADE DO CLIENT, não a uma flag de módulo.
 *
 * Por que WeakMap (achado da revisão do plano, 2026-09-03): uma flag global vazaria em três
 * caminhos reais — (a) o `ACTION_RESET_CONVERSATION` é despachado ANTES de o `stateChanged`
 * assíncrono do SDK chegar, então limpá-la no reset a apagaria cedo demais e o alerta sairia
 * rotulado ao contrário; (b) uma chamada que retorna cedo por `emAndamento` deixaria a marca para a
 * reconexão SEGUINTE; (c) o shutdown passa por `disconnecting` antes de `disconnected`, então
 * consumir na primeira mudança de estado perderia a marca.
 *
 * Presa ao client, a marca abandonada morre com ele: o client da conversa seguinte é outro objeto e
 * nasce sem rótulo. Rotular errado é PIOR que não rotular — a operação passaria a confiar num dado
 * falso.
 */
const origemPorClient = new WeakMap<Client, OrigemDaReconexao>();

/** Marca a origem no client. Chamado antes de um desligamento nosso, deliberado. */
export const marcarOrigemDaReconexao = (client: Client, origem: OrigemDaReconexao): void => {
    if (client) {
        origemPorClient.set(client, origem);
    }
};

/**
 * Descarta a marca sem reportar.
 *
 * 🔴 Existe por achado da revisão do código: se o `shutdown()` falhar, ou se a reconexão
 * TIVER SUCESSO, a marca ficaria no client — e uma queda REAL futura desse mesmo client sairia
 * rotulada como `desligamento_proprio`. `WeakMap` não força coleta: ele só não retém a chave, e o
 * SDK pode manter o client vivo por socket, timer ou listener. Falso negativo (alerta sem rótulo) é
 * muito melhor que afirmar uma causa falsa.
 */
export const descartarOrigemDaReconexao = (client: Client): void => {
    if (client) {
        origemPorClient.delete(client);
    }
};

/** Lê e CONSOME a marca. Consumida só no desfecho terminal, nunca em mudança de estado. */
const consumirOrigem = (client: Client): OrigemDaReconexao | undefined => {
    const marcada = origemPorClient.get(client);
    if (marcada) {
        origemPorClient.delete(client);
    }
    return marcada;
};

export const MAX_TENTATIVAS = 3;
const BACKOFF_MS = 5000;

/*
 * O contador vive no MÓDULO, não na chamada: o Tier 2 constrói um client novo e re-registra os
 * listeners, então um contador por-chamada zeraria a cada rodada e a reconexão tentaria para
 * sempre. Zera só quando a conexão volta de verdade.
 */
let tentativas = 0;
let emAndamento = false;

/*
 * A qual conversa o orçamento de `tentativas` pertence.
 *
 * Sem isto o contador era global e eterno: esgotado numa conversa, seguia esgotado na seguinte, e o
 * apostador que abria um chat novo perdia a reconexão inteira na primeira queda — sem uma tentativa
 * sequer, e sem nada na tela explicando. É a mesma raiz do bug do `visibilitychange` (o contador
 * travado no máximo matava o gatilho); aqui o gatilho é a conversa nova.
 *
 * "Esgotou" sempre quis dizer "pare de insistir NESTA conversa", nunca "nunca mais reconecte".
 */
let conversaDoOrcamento: string | null = null;

/*
 * 📌 Por que este estado fica no módulo e NÃO no Redux — decidido na codereview de 2026-08-28,
 * não esquecido:
 *
 *  - `emAndamento` é um **mutex de reentrância**, não estado de tela. O Redux existe para dado
 *    serializável que dirige render; um mutex lá faria cada tentativa despachar ação só para
 *    contabilidade, e nenhum componente leria isso.
 *  - `tentativas` também não é lido por componente nenhum. Iria para o store só para satisfazer a
 *    forma — e ainda precisaria ser zerado entre testes.
 *  - E o argumento mais forte: esta lógica foi validada **contra o Twilio real**, onde dois bugs
 *    meus só apareceram no navegador (desistir na primeira falha de token, e o contador travado no
 *    máximo matando o `visibilitychange`). Reescrever sem necessidade funcional reabriria esse
 *    risco num produto sem staging — a regra do repo é "não refatore sem necessidade".
 *
 * O custo aceito é o hook abaixo: três linhas, explícito, com nome que não engana.
 */

/** Uso exclusivo dos testes — o estado de módulo não é resetado pelo `resetMocks` do CRA. */
export const resetarTentativasParaTeste = (): void => {
    tentativas = 0;
    emAndamento = false;
    conversaDoOrcamento = null;
};

/**
 * `retrying` de fora: é o SDK se recuperando sozinho de uma queda curta (comportamento já testado e
 * funcional). Disparar nossa reconexão em cima disso cria corrida com a dele.
 */
export const deveReconectar = (estado: string): boolean => ESTADOS_QUE_DISPARAM.includes(estado);

const dormir = (ms: number): Promise<void> =>
    new Promise((resolve) => {
        setTimeout(resolve, ms);
    });

export interface OpcoesReconexao {
    client: Client;
    dispatch: Dispatch;
    conversationSid: string;
    /** Injetável no teste para não esperar o backoff real. */
    esperar?: (ms: number) => Promise<void>;
    /**
     * Zera o orçamento de tentativas antes de começar. Use quando o gatilho é uma NOVA
     * oportunidade dirigida pelo cliente (ele voltou pra aba), não a nossa própria insistência:
     * esgotar o orçamento significa "pare de tentar SOZINHO", não "nunca mais tente".
     * Sem isto, a volta da aba depois de uma falha terminal nascia morta.
     */
    reiniciarTentativas?: boolean;
    /**
     * De onde veio o gatilho, do ponto de vista do CHAMADOR. A marca no client (posta antes de um
     * desligamento nosso) tem precedência sobre isto — ela conhece a causa real.
     */
    origem?: OrigemDaReconexao;
}

export const reconectar = async ({
    client,
    dispatch,
    conversationSid,
    esperar = dormir,
    reiniciarTentativas = false,
    origem
}: OpcoesReconexao): Promise<void> => {
    // Reentrância: vários `connectionStateChanged` chegam em rajada; uma reconexão por vez.
    if (emAndamento) {
        return;
    }
    /*
     * Conversa nova = orçamento novo. Vem ANTES do `reiniciarTentativas` de propósito: são gatilhos
     * independentes (aquele é a volta da aba, este é outra conversa) e nenhum deve depender do outro.
     */
    if (conversaDoOrcamento !== conversationSid) {
        conversaDoOrcamento = conversationSid;
        tentativas = 0;
    }
    if (reiniciarTentativas) {
        tentativas = 0;
    }
    emAndamento = true;
    // O texto já promete "Tentando reconectar." — serve para o caminho de retry sem copy nova.
    dispatch(addNotification(notifications.noConnectionNotification()));

    try {
        while (tentativas < MAX_TENTATIVAS) {
            const primeiraTentativa = tentativas === 0;
            tentativas += 1;

            const data = await sessionDataHandler.getUpdatedToken().catch(() => null);
            /*
             * Sem token novo, NÃO desistimos na hora: `getUpdatedToken` lança igual para 403 (token
             * realmente expirado, irrecuperável) e para backend momentaneamente fora (recuperável),
             * e daqui não há como distinguir — ele embrulha tudo em `Error("Something went wrong…")`.
             * Entre desistir de um blip de 10s e gastar 2 tentativas a mais num token vencido, a
             * segunda opção é muito mais barata pro apostador. Então conta como tentativa falha e
             * segue pro backoff. (Validado no navegador: com o backend derrubado, a versão anterior
             * mostrava a falha terminal em 2s, sem tentar novamente.)
             */
            if (!data?.token) {
                log.warn("reconnect: refreshToken falhou — vai repetir");
                if (tentativas < MAX_TENTATIVAS) {
                    await esperar(BACKOFF_MS);
                }
                continue;
            }

            try {
                if (primeiraTentativa) {
                    // Tier 1: leve. Não recria client nem estado, então não duplica nada.
                    await client.updateToken(data.token);
                    if (client.connectionState === "connected") {
                        tentativas = 0;
                        // Reconectou: a marca não serve mais e não pode rotular uma queda futura.
                        descartarOrigemDaReconexao(client);
                        dispatch(removeNotification(notifications.noConnectionNotification().id));
                        /*
                         * O socket voltou, mas os eventos perdidos durante a queda NÃO voltam — e o
                         * SDK não se re-sincroniza sozinho (medido). Sem esta releitura o cliente
                         * volta pra uma tela que diz "ativo" numa conversa já encerrada.
                         * (O Tier 2 não precisa: o `initSession` já lê tudo do zero.)
                         */
                        /*
                         * 🔴 O DESFECHO É CONSULTADO — releitura que não conclui não é sucesso.
                         *
                         * O socket voltou, mas se a releitura não conclui a tela pode continuar
                         * dizendo "ativo" numa conversa já encerrada: exatamente o bug que a
                         * revalidação existe para fechar. Uma segunda tentativa limitada é barata e
                         * cobre o caso comum — a rede acabou de voltar e ainda estava instável.
                         *
                         * NÃO travamos o composer no "desconhecido", de propósito: bloquear o envio
                         * quando só não sabemos puniria o caso comum (blip de rede numa conversa
                         * saudável) para proteger o raro. Se a conversa realmente fechou, o 400 do
                         * Twilio já tem tratamento próprio. A falha das duas tentativas fica
                         * contável no log via `revalidate_failed`.
                         */
                        /*
                         * 🔴 A REVALIDAÇÃO FICA ISOLADA NUM `try` PRÓPRIO — e isto não é zelo, é
                         * correção de loop infinito.
                         *
                         * `tentativas` já foi zerado acima. Se qualquer coisa aqui lançar, o
                         * `return` abaixo é PULADO e a execução cai no `catch` do laço com o
                         * contador em zero: `while (tentativas < MAX_TENTATIVAS)` deixa de ter fim.
                         * Na prática o widget retenta reconexão para sempre — rajada de refresh de
                         * token contra o backend, CPU do navegador do apostador presa, e nenhuma
                         * tela de erro, porque do ponto de vista do código ele está "tentando".
                         *
                         * Revalidar é DIAGNÓSTICO. Falhar aqui não pode reabrir o ciclo.
                         */
                        try {
                            const releitura = await revalidarEstadoDaConversa({ client, conversationSid, dispatch });
                            if (releitura?.ok !== true) {
                                /*
                                 * `void`: a segunda tentativa NÃO segura o retorno da reconexão. O
                                 * socket já voltou e o aviso já saiu da tela — fazer o apostador
                                 * esperar mais 5s por um diagnóstico nosso seria pagar com a
                                 * experiência dele por informação nossa.
                                 */
                                void esperar(BACKOFF_MS)
                                    .then(() => revalidarEstadoDaConversa({ client, conversationSid, dispatch }))
                                    // A 2ª tentativa é solta: uma rejeição dela não pode virar `unhandledrejection`.
                                    .catch(() => undefined);
                            }
                        } catch {
                            /* diagnóstico não reabre o ciclo de reconexão — ver o bloco acima */
                        }
                        return;
                    }
                } else {
                    /*
                     * Tier 2: reconstrói. O `shutdown()` do client velho vem ANTES e é
                     * obrigatório — dois clients vivos emitem o mesmo `messageAdded` e a
                     * mensagem aparece DUPLICADA pro apostador, que é pior que o chat mudo.
                     */
                    await client.shutdown().catch(() => undefined);
                    /*
                     * 🔴 O desfecho é CONSULTADO, não deduzido da resolução.
                     *
                     * O `initSession` trata a falha por dentro — reporta, avisa e devolve o
                     * apostador ao formulário — e então RESOLVE. Ler resolução como sucesso zerava
                     * as tentativas e engolia o `reconnect_failed`: a recuperação desistia e
                     * ninguém ficava sabendo. Só `ok: true` conta como recuperado.
                     */
                    const desfecho = (await dispatch(
                        initSession({ token: data.token, conversationSid }) as never
                    )) as unknown as { ok: boolean } | undefined;
                    if (desfecho?.ok !== true) {
                        throw new Error("initSession não recuperou a sessão");
                    }
                    tentativas = 0;
                    // Idem Tier 1: sessão reconstruída, a marca do client velho morre aqui.
                    descartarOrigemDaReconexao(client);
                    dispatch(removeNotification(notifications.noConnectionNotification().id));
                    return;
                }
            } catch (e) {
                log.warn(`reconnect: tentativa falhou: ${descreverErro(e)}`);
            }

            if (tentativas < MAX_TENTATIVAS) {
                await esperar(BACKOFF_MS);
            }
        }

        /*
         * Esgotou. Troca a promessa de "tentando reconectar" por uma falha terminal — deixar o aviso
         * antigo na tela mentiria que ainda há tentativa em curso. E PARA de tentar: insistir sozinho
         * para sempre gasta bateria e nunca informa o cliente.
         */
        /*
         * A marca do client vence o parâmetro: quem marcou sabe a causa real (fomos nós que
         * desligamos), enquanto o chamador só enxerga o sintoma (`disconnected`).
         *
         * 🔴 Lida ANTES de avisar a tela, e a ordem é o ponto. Enquanto ela era consultada só junto
         * do report, o aviso já tinha ido para a tela sem que este caminho soubesse quem derrubou a
         * conexão.
         */
        const origemReal = consumirOrigem(client) ?? origem;

        dispatch(removeNotification(notifications.noConnectionNotification().id));
        /*
         * 🔴 QUANDO FOMOS NÓS QUE DESLIGAMOS, O APOSTADOR NÃO VÊ ERRO NENHUM.
         *
         * Medido contra o Twilio real em 2026-09-06: ao clicar em "Iniciar nova conversa" o
         * `shutdown()` derruba a conexão de propósito, a reconexão desiste, e o widget mostrava
         * "Não foi possível reconectar. Recarregue a página para continuar o atendimento." na TELA
         * DE BOAS-VINDAS — onde nada está quebrado e o botão de iniciar funciona. Mandar recarregar
         * ali é pedir ao apostador que conserte um problema que não existe.
         *
         * Nas outras origens o aviso continua: quando a conexão cai de verdade, ele é a única coisa
         * que explica por que o chat parou. E a telemetria sai nos dois casos — calar a TELA não é
         * ficar cego.
         */
        if (origemReal !== "desligamento_proprio") {
            dispatch(addNotification(notifications.failedToReconnectNotification()));
        }
        /*
         * Auditoria de observabilidade (2026-08-28): a falha terminal avisava o cliente na tela mas
         * não reportava nada — não sabíamos quantas vezes o widget DESISTE, que é a métrica que diz
         * se a reconexão está resolvendo ou só maquiando. Gap meu, do commit `d15c39b`.
         */
        reportClientError("reconnect_failed", {
            reason: `desistiu depois de ${MAX_TENTATIVAS} tentativas`,
            ...(origemReal ? { origemDaReconexao: origemReal } : {})
        });
    } finally {
        emAndamento = false;
    }
};
